import { GROUNDWORKS_TAKEOFF_COLUMN_MAP } from "./groundworksTakeoffColumnMap";

export interface TakeoffValidationResult {
  isValid: boolean;
  errors: string[];
  /** True when none of the expected worksheets were found at all — the
   * uploaded file isn't a Groundworks take-off workbook (wrong file entirely),
   * rather than a real take-off file with a few sheets/columns out of sync
   * with the template. Callers should show a plain "wrong file" message
   * instead of the itemized `errors` list in this case. */
  isWrongFileType: boolean;
}

/**
 * Validates an uploaded Groundworks take-off workbook against
 * `GROUNDWORKS_TAKEOFF_COLUMN_MAP` (derived from `public/GWKS Take-off
 * sheet.xlsx`, which the template itself treats as each sheet's column
 * identity — see that file's own "Notes" sheet). For every mapped sheet:
 *  - A sheet missing entirely fails.
 *  - Every mapped column's `key` must be found at its exact `cellRef` in the
 *    uploaded sheet (a direct cell-address lookup, e.g. `worksheet["D1"]`) —
 *    not merely present somewhere in the sheet. This single position check
 *    also catches a column inserted anywhere before an existing key, keyed
 *    or not: doing so shifts every key after it off its expected cell,
 *    failing this check for all of them, without needing a separate
 *    extra-column or sheet-width check.
 *  - A column's human-readable `label` (currently captured for "PLOT C&F
 *    QUANTS" only — see `TakeoffColumnDefinition`) remains informational
 *    only, never checked against the uploaded sheet — a real export can
 *    legitimately rename the header text while keeping the same key/cell.
 */
export async function validateGroundworksTakeoff(
  file: File,
): Promise<TakeoffValidationResult> {
  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  const errors: string[] = [];
  const sheetEntries = Object.entries(GROUNDWORKS_TAKEOFF_COLUMN_MAP);
  let missingSheetCount = 0;

  for (const [sheetName, columns] of sheetEntries) {
    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) {
      errors.push(`Missing worksheet: "${sheetName}"`);
      missingSheetCount++;
      continue;
    }

    let mismatchedColumn: (typeof columns)[number] | null = null;
    for (const column of columns) {
      const cell = worksheet[column.cellRef];
      const value = cell !== undefined ? String(cell.v).trim() : "";
      if (value !== column.key) {
        mismatchedColumn = column;
        break;
      }
    }
    if (mismatchedColumn) {
      const columnDescriptor = mismatchedColumn.label
        ? `"${mismatchedColumn.label}" (cell ${mismatchedColumn.cellRef})`
        : `at cell ${mismatchedColumn.cellRef}`;
      errors.push(
        `Worksheet "${sheetName}" has a column that doesn't match the take-off template — mismatch at column ${columnDescriptor}`,
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    isWrongFileType: missingSheetCount === sheetEntries.length,
  };
}
